import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { Analytics } from '@segment/analytics-next';
import { useIntercom, IntercomProvider } from 'react-use-intercom';
import { Events } from '@services/Events';
import { useTrackAnalyticsMutation } from '@api/graphql-codegen';
import { isHomeEDApp } from '@src/constants';
import { logger } from '../utils/logger';
import MixpanelSingleton from '../services/MixpanelSingleton';
import SegmentSingleton from '../services/SegmentSingleton';

const SegmentKey = import.meta.env.VITE_SEGMENT_KEY;
const IntercomAppID = import.meta.env.VITE_INTERCOM_APP_ID;

// Create a Context with undefined initially
const SegmentContext = createContext<Analytics | undefined>(undefined);

if (!IntercomAppID) {
  logger.warn('Intercom app id not provided. Intercom features will be disabled.');
}
// Don't throw an error if SegmentKey is missing or invalid
// Just log a warning and continue without analytics
if (!SegmentKey) {
  logger.warn('SegmentKey is not provided. Analytics will be disabled.');
}
const mixpanelToken = import.meta.env.VITE_MIXPANEL_TOKEN;
const mixpanelSecret = import.meta.env.VITE_MIXPANEL_API_SECRET;
if (!mixpanelToken) {
  logger.warn('Mixpanel Token is not provided.');
}

if (!mixpanelSecret) {
  logger.warn('Mixpanel API Secret is not provided.');
}
export type AnalyticsProviderProps = {
  children: React.ReactNode;
};

const useAnalytics = () => {
  const { trackEvent: intercomTrackEvent, update } = useIntercom();
  const segment = useContext(SegmentContext);
  const [trackAnalytics] = useTrackAnalyticsMutation();

  const trackEvent = useCallback(async (event: string, params?: any, mixpanelOnly?: boolean, retries = 3, delayMs = 1000) => {
    if (!mixpanelOnly) {
      intercomTrackEvent(event, params);
    }

    if (SegmentKey && segment) {
      for (let i = 0; i < retries; i++) {
        try {
          logger.debug(`Attempt ${i + 1}: Tracking event ${event}`);
          await new Promise((resolve, reject) =>
            segment.track(event, params, undefined, resolve).catch(reject)
          );
          logger.debug(`Event ${event} tracked successfully.`);
          break;
        } catch (e) {
          logger.error(`Segment tracking attempt ${i + 1} failed for event ${event}:`, e);
          if (i === retries - 1) {
            logger.error(`All ${retries} retries failed for event ${event}.`);
          } else {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }
        }
      }
    }
    // Remove mixpanel track, just keep segment track
    // if (mixpanel) {
    //   for (let i = 0; i < retries; i++) {
    //     try {
    //       logger.debug(`Attempt ${i + 1}: Tracking event ${event}`);
    //       mixpanel.track(event, params);
    //       logger.debug(`Event ${event} tracked successfully.`);

    //     logger.info(`[Mixpanel Event Tracking]: ${event}`, params);
    //   } catch (e) {
    //       logger.error(`Mixpanel tracking attempt ${i + 1} failed for event ${event}:`, e);
    //       if (i === retries - 1) {
    //         logger.error(`All ${retries} retries failed for event ${event}.`);
    //       } else {
    //         await new Promise((resolve) => setTimeout(resolve, delayMs));
    //       }
    //     }
    //   }
    // } else {
    //   logger.warn('Mixpanel not initialized. Skipping trackEvent:', event);
    // }
  }, [intercomTrackEvent, segment, trackAnalytics]);

  const setUserData = useCallback(async (userId: string, traits?: Record<string, any>, mixpanelOnly?: boolean, retries = 3, delayMs = 1000) => {
    if (!mixpanelOnly) {
      update({ userId, ...(traits ?? {}) });
    }

    if (SegmentKey && segment) {
      for (let i = 0; i < retries; i++) {
        try {
          logger.debug(`Attempt ${i + 1}: Identifying user ${userId}`);
          await segment.identify(userId, traits);
          logger.debug('User identified successfully.');
          break;
        } catch (e) {
          logger.error(`Segment identification attempt ${i + 1} failed:`, e);
          if (i === retries - 1) {
            logger.error(`All ${retries} retries failed for user identification.`);
          } else {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }
        }
      }
    }
  }, [update, segment]);

  const identify = async(userId: string, userProps?: Record<string, any>, retries = 3, delayMs = 1000) => {
    // Ensure Mixpanel singleton is initialized before identifying
    const mixpanelSingleton = MixpanelSingleton.getInstance();
    
    try {
      await mixpanelSingleton.initialize();
    } catch (error) {
      logger.error('Failed to initialize Mixpanel for user identification:', error);
      return;
    }

    if (!mixpanelSingleton.isInitialized()) {
      logger.error('Mixpanel is not initialized. Skipping User Identify', userId);
      return;
    }

    const mixpanel = mixpanelSingleton.getMixpanel();

    for (let i = 0; i < retries; i++) {
      try {
        logger.debug(`Attempt ${i + 1}: Identifying user ${userId}`);
        mixpanel.identify(userId);
        if (userProps) {
          mixpanel.people.set(userProps);
        }
        logger.debug(`Mixpanel User ${userId} identified successfully.`);
        break;
      } catch (e) {
        logger.error(`Mixpanel identification attempt ${i + 1} failed for user ${userId}:`, e);
        if (i === retries - 1) {
          logger.error(`All ${retries} retries failed for user ${userId}.`);
        } else {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }
    }
  };

  return {
    identify,
    trackEvent,
    setUserData,
    segment,
  };
}



const SegmentProvider = ({ children }: { children: React.ReactNode }) => {
  const [analytics, setAnalytics] = useState<Analytics | undefined>(undefined);

  useEffect(() => {
    // Use singleton pattern for Segment initialization
    const segmentSingleton = SegmentSingleton.getInstance();
    segmentSingleton.initialize()
      .then(() => {
        const analyticsInstance = segmentSingleton.getAnalytics();
        setAnalytics(analyticsInstance);
      })
      .catch(error => {
        logger.error('Failed to initialize Segment singleton:', error);
      });
  }, []);

  useEffect(() => {
    // Use singleton pattern for Mixpanel initialization
    const mixpanelSingleton = MixpanelSingleton.getInstance();
    mixpanelSingleton.initialize().catch(error => {
      logger.error('Failed to initialize Mixpanel singleton:', error);
    });
  }, []);



  return <SegmentContext.Provider value={analytics}>{children}</SegmentContext.Provider>;
};

const useSegment = () => {
  const segment = useContext(SegmentContext);
  return { segment };
};

const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  // Use a default empty string if IntercomAppID is undefined
  const intercomAppId = IntercomAppID || '';
  
  return (
    <IntercomProvider appId={intercomAppId}>
      <SegmentProvider>
        {children}
      </SegmentProvider>
    </IntercomProvider>
  )
};

export {
  AnalyticsProvider,
  Events,
  useAnalytics,
  useSegment,
}
